import React, { useEffect } from 'react'
import { useRouteError } from 'react-router-dom'

import { reportError } from '../utils/errorTracking'

/**
 * Friendly, dependency-light fallback shown when the app fails to render or a code-split
 * chunk fails to load. Kept in plain English and free of app providers (IntlProvider,
 * TailwindContext, …) on purpose: it must render even when those never mounted.
 */
export default function AppErrorFallback () {
  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center min-h-screen gap-6 p-6 text-center"
    >
      <div className="max-w-sm space-y-2">
        <h1 className="text-lg font-bold text-ternary">Something went wrong</h1>
        <p className="text-ternary">
          We could not finish loading the page. Please check your connection and reload.
        </p>
      </div>
      <button
        type="button"
        autoFocus
        onClick={() => window.location.reload()}
        className="min-h-10 px-5 py-2 font-bold text-white transition rounded-md bg-primary hover:bg-primary-dark focus:outline-none focus:ring-3 ring-primary-light"
      >
        Reload page
      </button>
    </div>
  )
}

/**
 * `errorElement` for the data router. `RouterProvider` catches route render errors
 * internally, so this is what turns the default "Unexpected Application Error!" screen
 * into our fallback — while still forwarding the error to Sentry.
 */
export function RouteErrorBoundary () {
  const error = useRouteError()

  useEffect(() => {
    reportError(error instanceof Error ? error : new Error(String(error)))
  }, [error])

  return <AppErrorFallback />
}
